import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CurrentInventoryDetailRow } from '../types';
import { getInventoryRowKey } from '../utils';

type UseCurrentInventorySelectionParams = {
  filteredDetails: CurrentInventoryDetailRow[];
  manualActionActive: boolean;
  preTrackingActionActive: boolean;
  showManualSoldOut: boolean;
  showPreTrackingSoldOut: boolean;
};

export function useCurrentInventorySelection({
  filteredDetails,
  manualActionActive,
  preTrackingActionActive,
  showManualSoldOut,
  showPreTrackingSoldOut,
}: UseCurrentInventorySelectionParams) {
  const [selectedPackKeys, setSelectedPackKeys] = useState<Set<string>>(() => new Set());

  const isManualSoldOutActionRow = useCallback(
    (row: CurrentInventoryDetailRow) => {
      if (!manualActionActive) {
        return false;
      }
      return showManualSoldOut
        ? row.is_manual_sold_out === true
        : row.can_mark_manual_sold_out === true;
    },
    [manualActionActive, showManualSoldOut]
  );

  const isInventoryTrackingActionRow = useCallback(
    (row: CurrentInventoryDetailRow) => {
      if (!preTrackingActionActive) {
        return false;
      }
      return showPreTrackingSoldOut
        ? row.is_sold_out_before_inventory_tracking === true
        : row.can_mark_sold_out_before_inventory_tracking === true;
    },
    [preTrackingActionActive, showPreTrackingSoldOut]
  );

  const manualSelectableFilteredDetails = useMemo(
    () => filteredDetails.filter(isManualSoldOutActionRow),
    [filteredDetails, isManualSoldOutActionRow]
  );

  const inventoryTrackingSelectableFilteredDetails = useMemo(
    () => filteredDetails.filter(isInventoryTrackingActionRow),
    [filteredDetails, isInventoryTrackingActionRow]
  );

  const showManualBulkAction = manualSelectableFilteredDetails.length > 0;
  const showPreTrackingBulkAction = inventoryTrackingSelectableFilteredDetails.length > 0;
  const selectionEnabled = showManualBulkAction || showPreTrackingBulkAction;

  const isSelectableRow = useCallback(
    (row: CurrentInventoryDetailRow) => {
      if (showManualBulkAction && isManualSoldOutActionRow(row)) {
        return true;
      }
      if (showPreTrackingBulkAction && isInventoryTrackingActionRow(row)) {
        return true;
      }
      return false;
    },
    [
      isInventoryTrackingActionRow,
      isManualSoldOutActionRow,
      showManualBulkAction,
      showPreTrackingBulkAction,
    ]
  );

  const selectableFilteredDetails = useMemo(
    () => filteredDetails.filter(isSelectableRow),
    [filteredDetails, isSelectableRow]
  );

  useEffect(() => {
    setSelectedPackKeys((current) => {
      if (current.size === 0) {
        return current;
      }
      const availableKeys = new Set(selectableFilteredDetails.map(getInventoryRowKey));
      const next = new Set(Array.from(current).filter((key) => availableKeys.has(key)));
      return next.size === current.size ? current : next;
    });
  }, [selectableFilteredDetails]);

  const selectedRows = useMemo(
    () => selectableFilteredDetails.filter((row) => selectedPackKeys.has(getInventoryRowKey(row))),
    [selectableFilteredDetails, selectedPackKeys]
  );

  const selectedManualSoldOutRows = useMemo(
    () => selectedRows.filter(isManualSoldOutActionRow),
    [isManualSoldOutActionRow, selectedRows]
  );

  const selectedInventoryTrackingRows = useMemo(
    () => selectedRows.filter(isInventoryTrackingActionRow),
    [isInventoryTrackingActionRow, selectedRows]
  );

  const handleToggleRowSelection = useCallback((row: CurrentInventoryDetailRow) => {
    if (!isSelectableRow(row)) {
      return;
    }
    const rowKey = getInventoryRowKey(row);
    setSelectedPackKeys((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }, [isSelectableRow]);

  const handleToggleAllSelection = useCallback((rows: CurrentInventoryDetailRow[]) => {
    setSelectedPackKeys((current) => {
      const rowKeys = rows.map(getInventoryRowKey);
      const allSelected = rowKeys.length > 0 && rowKeys.every((key) => current.has(key));
      const next = new Set(current);
      rowKeys.forEach((key) => {
        if (allSelected) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  }, []);

  const clearSelectedPackKeys = useCallback(() => {
    setSelectedPackKeys(new Set());
  }, []);

  return {
    selectedPackKeys,
    selectedRows,
    selectedManualSoldOutRows,
    selectedInventoryTrackingRows,
    selectionEnabled,
    showManualBulkAction,
    showPreTrackingBulkAction,
    isSelectableRow,
    handleToggleRowSelection,
    handleToggleAllSelection,
    clearSelectedPackKeys,
  };
}
